import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API } from '@/lib/api';
import { toast } from 'sonner';
import { FileText, Download, Sparkles, HelpCircle } from 'lucide-react';
import Navigation from '@/components/Navigation';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

function ScriptGenerator() {
  const [scenarioType, setScenarioType] = useState('salary');
  const [details, setDetails] = useState({
    current: '',
    desired: '',
    context: ''
  });
  const [generating, setGenerating] = useState(false);
  const [script, setScript] = useState(null);

  const scenarioTypes = [
    { value: 'salary', label: 'Salary Negotiation' },
    { value: 'bill', label: 'Bill Reduction' },
    { value: 'purchase', label: 'Purchase/Price Negotiation' },
    { value: 'freelance', label: 'Freelance Rate' },
    { value: 'contract', label: 'Contract Terms' }
  ];

  const generateScript = async () => {
    if (!details.current || !details.desired) {
      toast.error('Please fill in all required fields');
      return;
    }

    setGenerating(true);
    try {
      const response = await API.post('/scripts/generate', {
        scenario_type: scenarioType,
        details
      });
      setScript(response.data);
      toast.success('Script generated!');
    } catch (error) {
      toast.error('Failed to generate script');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navigation />
      
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <FileText className="text-green-500 w-16 h-16 mx-auto mb-4" />
          <h1 className="font-['Outfit'] text-4xl font-bold mb-3" data-testid="script-generator-title">Script Generator</h1>
          <p className="text-gray-400">Get AI-powered negotiation scripts tailored to your situation</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="ninja-card">
            <h2 className="font-['Outfit'] text-2xl font-bold mb-6">Scenario Details</h2>
            
            <div className="mb-6">
              <Label className="mb-2 block">Scenario Type</Label>
              <div className="space-y-2">
                {scenarioTypes.map(type => (
                  <button
                    key={type.value}
                    onClick={() => setScenarioType(type.value)}
                    className={`w-full p-3 rounded-lg text-left border transition-all ${
                      scenarioType === type.value ? 'border-green-500 bg-green-500/10' : 'border-white/10 hover:bg-white/5'
                    }`}
                    data-testid={`scenario-type-${type.value}`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <Label htmlFor="current" className="mb-2 block">Current Situation *</Label>
              <Input
                id="current"
                value={details.current}
                onChange={(e) => setDetails({...details, current: e.target.value})}
                placeholder="e.g., Current salary: $60,000"
                className="bg-black/50 border-white/20"
                data-testid="current-situation-input"
              />
            </div>

            <div className="mb-4">
              <Label htmlFor="desired" className="mb-2 block">Desired Outcome *</Label>
              <Input
                id="desired"
                value={details.desired}
                onChange={(e) => setDetails({...details, desired: e.target.value})}
                placeholder="e.g., Target salary: $70,000"
                className="bg-black/50 border-white/20"
                data-testid="desired-outcome-input"
              />
            </div>

            <div className="mb-6">
              <Label htmlFor="context" className="mb-2 block">Additional Context</Label>
              <Input
                id="context"
                value={details.context}
                onChange={(e) => setDetails({...details, context: e.target.value})}
                placeholder="e.g., Performance reviews excellent, 2 years at company"
                className="bg-black/50 border-white/20"
                data-testid="context-input"
              />
            </div>

            <Button 
              onClick={generateScript} 
              disabled={generating}
              className="w-full bg-green-600 hover:bg-green-500"
              data-testid="generate-script-button"
            >
              {generating ? 'Generating...' : (
                <><Sparkles className="w-5 h-5 mr-2" /> Generate Script</>
              )}
            </Button>
          </div>

          <div className="ninja-card">
            {!script ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <FileText className="text-gray-600 w-24 h-24 mb-4" />
                <p className="text-gray-400">Your generated script will appear here</p>
              </div>
            ) : (
              <div data-testid="generated-script">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-['Outfit'] text-2xl font-bold">Your Script</h2>
                  <div className="text-right">
                    <div className="flex items-center gap-2 justify-end mb-1">
                      <div className="text-sm text-gray-400">Win Probability</div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-white cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs bg-[#1A1A1A] border border-white/20 p-4">
                            <p className="text-sm text-gray-300 mb-2">
                              <strong>How is this calculated?</strong>
                            </p>
                            <p className="text-xs text-gray-400 mb-2">
                              Our AI analyzes your scenario against thousands of real negotiation outcomes, considering factors like:
                            </p>
                            <ul className="text-xs text-gray-400 space-y-1 list-disc list-inside">
                              <li>Market conditions & industry standards</li>
                              <li>Your leverage points & preparation level</li>
                              <li>Opponent profile & historical patterns</li>
                              <li>Negotiation tactics suggested</li>
                            </ul>
                            <p className="text-xs text-green-400 mt-3">
                              🔒 Upgrade to Premium for advanced probability analysis with personalized risk factors
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="text-2xl font-bold text-green-500" data-testid="win-probability">{script.win_probability}%</div>
                  </div>
                </div>

                <div className="bg-black/30 border border-white/10 rounded-lg p-6 mb-4 max-h-96 overflow-y-auto">
                  <pre className="whitespace-pre-wrap font-['DM_Sans'] text-sm leading-relaxed" data-testid="script-text">
                    {script.script_text}
                  </pre>
                </div>

                <Button 
                  onClick={() => {
                    const blob = new Blob([script.script_text], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `negotiation-script-${Date.now()}.txt`;
                    a.click();
                    toast.success('Script downloaded!');
                  }}
                  variant="outline"
                  className="w-full border-white/20 hover:bg-white/10"
                  data-testid="download-script-button"
                >
                  <Download className="w-5 h-5 mr-2" /> Download Script
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ScriptGenerator;